<template>
  <navigation :config="config" :planning="planning" extended>
    <template #extension>
      <v-text-field v-if="planning.meta" v-model="planning.meta.title" :placeholder="$t('PLANNING.UNNAMED_PROJECT')" spellcheck="false"
                    density="compact" :readonly="! canModifyPlanning || ! editableTitle" rounded variant="solo" flat prepend-inner-icon="far fa-folder-open"
                    class="hovervisible-activator" :class="{ 'children-cursor-default': ! editableTitle }" style="flex: 3 0 200px"
                    @focus="startChangingMeta" @blur="changingMeta(); editableTitle = false">
        <template v-if="canModifyPlanning" #append-inner>
          <v-tooltip location="bottom">
            <template #activator="{ props }">
              <v-icon class="hovervisible-item" style="cursor: pointer !important;" v-bind="props" @click="editableTitle = true">far fa-pencil-alt</v-icon>
            </template>
            {{ $t('NAV.RENAME_PROJECT') }}
          </v-tooltip>
        </template>
        <template v-else-if="! view" #append-inner>
          <v-chip color="successgreen">
            <v-icon>far fa-eye</v-icon><span class="ml-1">{{ $t('ACR.CONSULT') }}</span>
          </v-chip>
        </template>
      </v-text-field>
      <v-text-field v-else density="compact" readonly rounded variant="solo" flat prepend-inner-icon="far fa-folder-open" style="flex: 3 0 200px"></v-text-field> <!-- placeholder -->

      <v-spacer class="mx-2"></v-spacer>

      <!--    PLANNING TOOLBAR-->
      <template v-if="config == 'planning' || config == 'planningview'">
        <v-btn v-draggable-button="{ enabled: true, type: 'task', start: draggableButtonStart, end: draggableButtonEnd }" size="small"
               :ripple="false" icon elevation="3" :class="{ invisible: projectReportIsOpened }" class="mx-1 tour-add-bubble grab touch-action-none">
          <div style="width: 24px; position: relative; border-radius: 4px; overflow: hidden">
            <div class="text-white task-default-blue" style="height: 8px; width: 100%; line-height: 0;">...</div>
            <div style="height: 14px; width: 100%; background: rgba(216, 216, 253, 0.7);"></div>
            <div></div>
          </div>
          <v-tooltip location="bottom" activator="parent">{{ $t('NAV.DRAG_AND_DROP_TO_ADD_BUBBLE') }}</v-tooltip>
        </v-btn>
        <v-btn v-draggable-button="{ enabled: true, type: 'milestone', start: draggableButtonStart, end: draggableButtonEnd }" size="small"
               :ripple="false" icon elevation="3" :class="{ invisible: projectReportIsOpened }" class="mx-1 tour-add-milestone grab touch-action-none">
          <div class="milestone-default-blue">
            <v-icon size="x-large">fak fa-bbp-milestone</v-icon>
          </div>
          <v-tooltip location="bottom" activator="parent">{{ $t('NAV.DRAG_AND_DROP_TO_ADD_MILESTONE') }}</v-tooltip>
        </v-btn>

        <div class="mx-2" style="flex: 0 1 16px"></div>

        <div :class="{ invisible: projectReportIsOpened }" class="bg-surface px-2 rounded-pill" style="transition: .28s opacity cubic-bezier(0.4, 0, 0.2, 1)">
          <v-btn-icon class="tour-save" size="small" @click="save()">
            <v-icon v-show="! uiSavingStore.inprogress && ! uiSavingStore.success && ! uiSavingStore.error" size="large">far fa-save</v-icon>
            <v-icon v-show="uiSavingStore.inprogress" size="large">far fa-spinner fa-spin</v-icon>
            <v-icon v-show="uiSavingStore.success" size="large">far fa-check text-successgreen</v-icon>
            <v-icon v-show="uiSavingStore.error" size="large" class="pointer">far fa-exclamation-triangle text-errorred</v-icon>
            <v-tooltip location="bottom" activator="parent">
              <span v-show="! uiSavingStore.inprogress && ! uiSavingStore.success && ! uiSavingStore.error">{{ $t('NAV.AUTOSAVE') }}</span>
              <span v-show="uiSavingStore.inprogress">{{ $t('AUTOSAVE.SAVING') }}</span>
              <span v-show="uiSavingStore.success">{{ $t('AUTOSAVE.SAVED') }}</span>
              <span v-show="uiSavingStore.error">{{ uiSavingStore.error }}</span>
            </v-tooltip>
          </v-btn-icon>
          <v-btn-icon :disabled="! undoable" size="small" @click="undo()">
            <v-icon size="large">far fa-undo</v-icon>
            <v-tooltip location="bottom" activator="parent">{{ $t('NAV.UNDO') }}</v-tooltip>
          </v-btn-icon>
          <v-btn-icon :disabled="! redoable" size="small" @click="redo()">
            <v-icon size="large">far fa-redo</v-icon>
            <v-tooltip location="bottom" activator="parent">{{ $t('NAV.REDO') }}</v-tooltip>
          </v-btn-icon>
          <planning-navigation-search :planning="planning" :disabled="! planningLoaded" @open-element="openElement"></planning-navigation-search>
          <template v-if="config == 'planning' && hasWorkingViews">
            <working-views-navigation v-if="planningLoaded" :planning="planning"></working-views-navigation>
            <v-btn-icon v-else disabled size="small"><v-icon size="large">far fa-filter</v-icon></v-btn-icon> <!-- button placeholder -->
          </template>
          <v-btn-icon v-else-if="config == 'planning'" :disabled="! planningLoaded" size="small" @click="openConfig">
            <!-- if no working view, use the space to add an openConfig shortcut -->
            <v-icon size="large">far fa-cog</v-icon>
            <v-tooltip location="bottom" activator="parent">{{ $t('PLANNING.PLANNING_CONFIG') }}</v-tooltip>
          </v-btn-icon>
          <template v-for="customNavigation in customNavigations" :key="customNavigation.id">
            <client-custom-component v-if="planningLoaded" :name="customNavigation.component" :planning="planning"></client-custom-component>
            <v-btn-icon v-else size="small"><v-icon size="large">{{ customNavigation.icon }}</v-icon></v-btn-icon>
          </template>
        </div>
        <v-snackbar :model-value="!! uiSavingStore.error && uiSavingStore.errorCount > 1" color="errorred" :max-width="560" :timeout="8000" close-on-content-click>
          <div class="flex-center-center"><v-icon start>far fa-exclamation-triangle</v-icon>{{ $t('AUTOSAVE.ERROR') }} {{ uiSavingStore.error }}</div>
        </v-snackbar>
      </template>
      <v-spacer v-else style="flex-grow: 2"></v-spacer>

      <v-spacer class="mx-2"></v-spacer>

      <!-- Planning Mode -->
      <btn-toggle-filter :class="{ 'events-disabled': ! planningLoadedWithSubprojects }" class="rounded-pill">
        <v-btn :active="planningLoaded && ! projectReportIsOpened && ! isKanbanMode" @click="setPlanningMode(null)">
          <v-icon>fak fa-bbp-planning</v-icon>
          <span class="hidden-md-and-down ml-2">{{ $t('NAV.PLANNING_VIEW') }}</span>
          <v-tooltip location="bottom" activator="parent" :disabled="! $vuetify.display.mdAndDown">{{ $t('NAV.PLANNING_VIEW') }}</v-tooltip>
        </v-btn>
        <v-btn :active="planningLoaded && ! projectReportIsOpened && isKanbanMode" @click="setPlanningMode('kanban')">
          <v-icon>fak fa-bbp-kanban</v-icon>
          <span class="hidden-md-and-down ml-2">{{ $t('NAV.KANBAN_VIEW') }}</span>
          <v-tooltip location="bottom" activator="parent" :disabled="! $vuetify.display.mdAndDown">{{ $t('NAV.KANBAN_VIEW') }}</v-tooltip>
        </v-btn>
        <v-btn :active="planningLoaded && projectReportIsOpened" @click="openProjectReport()">
          <v-icon>far fa-flag</v-icon>
          <span class="hidden-md-and-down ml-2">{{ $t('NAV.PROJECT_SHEET') }}</span>
          <v-tooltip location="bottom" activator="parent" :disabled="! $vuetify.display.mdAndDown">{{ $t('NAV.PROJECT_SHEET') }}</v-tooltip>
        </v-btn>
      </btn-toggle-filter>
      <div class="mx-2"></div>

      <!--    PLANNING ACTIONS-->
      <v-btn-group v-if="config == 'planning'" density="comfortable" rounded="pill">
        <v-btn variant="flat" size="large" class="tour-share pr-2" @click="openShare()">
          {{ $t('NAV.SHARE') }}
          <v-tooltip location="bottom" activator="parent">{{ $t('NAV.SHARE_THIS_PROJECT') }}</v-tooltip>
        </v-btn>
        <v-menu>
          <template #activator="{ props }">
            <v-btn variant="flat" size="large" class="tour-export pl-2 pr-3" v-bind="props">
              <v-icon>far fa-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="comfortable">
            <v-list-item @click="openExport()">
              {{ $t('NAV.EXPORT') }}
              <v-tooltip location="left" activator="parent">{{ $t('NAV.EXPORT_TO_IMAGE_FILE') }}</v-tooltip>
            </v-list-item>
            <v-list-item @click="openImport()">
              {{ $t('NAV.IMPORT') }}
              <v-tooltip location="left" activator="parent">{{ $t('PLANNING.IMPORT_TEMPLATE') }}</v-tooltip>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn-group>

      <v-btn v-else-if="config == 'planningview'" variant="flat" size="large" rounded class="tour-export" @click="openExport()">
        <span>{{ $t('NAV.EXPORT') }}</span>
        <v-tooltip location="bottom" activator="parent">{{ $t('NAV.EXPORT_TO_IMAGE_FILE') }}</v-tooltip>
      </v-btn>

      <!-- EXPORT / IMPORT MODAL : eager to preserve state of content -->
      <v-dialog v-if="planning.visibleTimeline" v-model="exportImportIsOpened" eager
                content-class="bg-background rounded" width="100%" height="100%">
        <div>
          <v-btn-icon size="small" color="medium-emphasis" class="float-right" style="margin-right: 1px" @click="openExportImport(null)">
            <v-icon size="24">far fa-times</v-icon>
          </v-btn-icon>
        </div>
        <div class="mr-3 px-6 pb-10 overflow-y-auto overflow-x-hidden overscroll-behavior-y-contain">
          <export-import-modal :planning="planning" :planning-view-id="planningViewId"></export-import-modal>
        </div>
      </v-dialog>
    </template>
  </navigation>
</template>

<style lang="scss">
  .children-cursor-default  * {
    cursor: default !important;
  }
</style>

<script>
  import { mapActions } from 'pinia';
  import clientConfig from '@/client_customs/config';
  import useSelectionStore from '@/stores/selection';
  import useUiPlanningStore from '@/stores/ui/planning';
  import useUiSavingStore from '@/stores/ui/saving';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import usePlanningStore from '@/stores/planning/planning';
  import usePlanningTimelineStore from '@/stores/planning/timeline';
  import usePlanningMetaStore from '@/stores/planning/meta';
  import useSubprojectsStore from '@/stores/subprojects';
  import useUndoRedoStore from '@/stores/undoredo';
  import Navigation from '@/components/Navigation/Navigation.vue';
  import WorkingViewsNavigation from '@/components/WorkingViews/WorkingViewsNavigation.vue';
  import ExportImportModal from '@/components/Export/ExportImportModal.vue';
  import PlanningNavigationSearch from './PlanningNavigationSearch.vue';

  export default {
    components: {
      Navigation,
      WorkingViewsNavigation,
      ExportImportModal,
      PlanningNavigationSearch,
    },
    props: {
      planning: { type: Object, required: true },
      dynamic: { type: Boolean, default: false },
      view: { type: Boolean, default: false },
      planningViewId: { type: Number, default: 0 },
    },
    data() {
      return {
        editableTitle: false,
        planningLoadedWithSubprojects: false, // switching to project report before subprojects are loaded causes height calculation issues in planning subprojects
        customNavigations: clientConfig.navigation?.planning,
        uiPlanningStore: useUiPlanningStore(),
        uiSavingStore: useUiSavingStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
        planningStore: usePlanningStore(),
        planningTimelineStore: usePlanningTimelineStore(),
        planningMetaStore: usePlanningMetaStore(),
        subprojectsStore: useSubprojectsStore(),
        undoredoStore: useUndoRedoStore(),
      };
    },
    computed: {
      config() {
        return this.view ? 'planningview' : this.dynamic ? 'planning' : '';
      },
      canModifyPlanning() {
        return this.dynamic && ! this.view;
      },
      undoable() {
        return this.undoredoStore.isUndoable;
      },
      redoable() {
        return this.undoredoStore.isRedoable;
      },
      planningLoaded() {
        return this.planning.visibleTimeline;
      },
      isKanbanMode() {
        return this.uiPlanningStore.mode == 'kanban';
      },
      exportImportIsOpened: {
        get() {
          return !! this.uiPlanningStore.exportImportOpeningTab;
        },
        set(newVal) {
          if (! newVal) this.uiPlanningStore.exportImportOpeningTab = '';
        },
      },
      hasWorkingViews() { return this.usersAccessRightStore.hasWorkingViews; },
      projectReportIsOpened() { return this.uiPlanningStore.projectReportIsOpened; },
      shortcutsEnabled() {
        return ! this.uiPlanningStore.projectReportIsOpened;
      },
    },
    watch: {
      planningLoaded(newVal) {
        this.planningLoadedWithSubprojects = false;
        if (! newVal) return;
        setTimeout(() => {
          Promise.all(this.subprojectsStore.promises).then(() => {
            setTimeout(() => {
              this.planningLoadedWithSubprojects = true;
            });
          });
        });
      },
    },
    mounted() {
      if (this.config == 'planning' || this.config == 'planningview') {
        window.Mousetrap.bind('mod+z', (event) => { event.preventDefault(); if (! this.shortcutsEnabled) return; this.undo(); });
        window.Mousetrap.bind('mod+y', (event) => { event.preventDefault(); if (! this.shortcutsEnabled) return; this.redo(); });
      }
    },
    unmounted() {
      const hotkeys = ['mod+z', 'mod+y'];
      for (let i = 0; i < hotkeys.length; i++) {
        window.Mousetrap.unbind(hotkeys[i]);
      }
    },
    methods: {
      undo() {
        if (! this.planningLoaded || ! this.undoable) return;
        this.undoredoStore.undo();
      },
      redo() {
        if (! this.planningLoaded || ! this.redoable) return;
        this.undoredoStore.redo();
      },
      setPlanningMode(newVal) {
        this.closeProjectReport();
        this.uiPlanningStore.mode = newVal;
        this.planningTimelineStore.set(this.planning.timeline);
        this.uiPlanningStore.savePlanningMode({ mode: newVal, planningId: this.planning.id });
        window.scrollTo({ top: 0 });
      },
      openProjectReport() {
        this.uiPlanningStore.openProjectReport();
        window.scrollTo({ top: 0 });
      },
      openShare() {
        if (! this.planningLoaded) return;
        this.openExportImport('share');
      },
      openExport() {
        if (! this.planningLoaded) return;
        this.openExportImport('export-image');
      },
      openImport() {
        if (! this.planningLoaded) return;
        this.openExportImport('import-template');
      },
      draggableButtonStart() {
        useSelectionStore().reset();
      },
      draggableButtonEnd(event, type) {
        const newElements = this.addElement({ type, el: null, x: event.dragData.x, y: event.dragData.y });
        if (newElements?.length) setTimeout(() => this.openElement(newElements[0]));
      },
      openElement(el) {
        this.uiPlanningStore.scrollToEl({ el, open: true });
      },
      ...mapActions(usePlanningStore, ['save']),
      ...mapActions(usePlanningMetaStore, ['startChangingMeta', 'changingMeta']),
      ...mapActions(useUiPlanningStore, ['addElement', 'openConfig', 'closeProjectReport', 'openExportImport']),
    },
  };
</script>
